import type { DigitalTwinsClient } from '@azure/digital-twins-core'
import { isRestError } from '@azure/core-rest-pipeline'
import type { Operation } from 'fast-json-patch'
import { digitalTwinsClientFactory } from './clients'

import type { IModelApiService } from 'src/stores/models'
import { useGlobalStore } from 'src/stores/global'
import type { BasicDigitalTwin } from 'models/BasicDigitalTwin'
import type { BasicRelationship } from 'models/BasicRelationship'
import type { DtdlInterface } from 'models/DtdlInterface'
import type { DigitalTwinsModelData } from 'models/DigitalTwinsModelData'
import { REL_TYPE_OUTGOING, REL_TYPE_INCOMING, REL_TYPE_ALL, QUERY_ALL_TWINS } from './constants'
import type { QueryResponseData } from 'src/utils/query-adt'
import { getDataFromQueryResponse } from 'src/utils/query-adt'

/**
 * Recovers the created models from a {@link RestError} that actually reports a
 * successful (2xx) response.
 *
 * The generated Azure Digital Twins client declares `POST /models` as `201 Created`
 * only, so a service answering with another 2xx status (Konnektr Graph returns `200 OK`)
 * is deserialized through the error mapper and thrown. The response body is still the
 * array of created models, so we can unwrap it instead of failing the operation.
 *
 * @returns the created models, or `undefined` when the error is a genuine failure.
 */
const modelsFromSuccessfulRestError = (e: unknown): DigitalTwinsModelData[] | undefined => {
  if (!isRestError(e)) return undefined
  const statusCode = e.statusCode ?? e.response?.status
  if (statusCode === undefined || statusCode < 200 || statusCode >= 300) return undefined

  const bodyAsText = e.response?.bodyAsText
  if (!bodyAsText) return []
  try {
    const parsed: unknown = JSON.parse(bodyAsText)
    return Array.isArray(parsed) ? (parsed as DigitalTwinsModelData[]) : []
  } catch {
    // A 2xx with an unparsable body still means the models were created; let the
    // caller refresh from the service rather than reporting a false failure.
    return []
  }
}

class TwinsApiService implements IModelApiService {
  private _client: DigitalTwinsClient | null = null

  public async query(
    query: string,
    callback: (response: QueryResponseData) => Promise<void> | void,
    abortController?: AbortController
  ) {
    await this.initialize()
    const queryResult = this._client?.queryTwins(query, {
      abortSignal: abortController?.signal
    })
    if (!queryResult) return
    for await (const page of queryResult.byPage()) {
      const data = getDataFromQueryResponse(page.value)
      await callback(data)
    }
  }

  public async queryTwins(query: string) {
    const result: QueryResponseData = {
      twins: [],
      relationships: [],
      count: 0,
      other: [],
      data: []
    }
    await this.query(query, ({ twins, relationships, count, other, data }) => {
      result.twins.push(...twins)
      result.relationships.push(...relationships)
      result.other.push(...other)
      result.data.push(...data)
      result.count = count
    })

    return result
  }

  public async getAllTwins() {
    return this.queryTwins(QUERY_ALL_TWINS)
  }

  public async getTwinById<T extends BasicDigitalTwin>(twinId: string) {
    await this.initialize()
    const response = await this._client?.getDigitalTwin(twinId)
    delete response?.etag
    return response as T
  }

  public async addTwin<T extends BasicDigitalTwin>(twinId: string, payload: T) {
    await this.initialize()
    const response = await this._client?.upsertDigitalTwin(twinId, JSON.stringify(payload))
    delete response?.etag
    return response as T
  }

  public async updateTwin(twinId: string, patch: Operation[]) {
    await this.initialize()

    return await this._client?.updateDigitalTwin(
      twinId,
      patch as unknown as Array<Record<string, unknown>>
    )
  }

  public async queryRelationshipsPaged(
    twinId: string,
    callback: (rels: BasicRelationship[]) => Promise<void> | void,
    type = REL_TYPE_OUTGOING
  ) {
    await this.initialize()
    const relationshipDirection = REL_TYPE_INCOMING ? 'target' : 'source'
    const query =
      type === REL_TYPE_ALL
        ? `SELECT * FROM RELATIONSHIPS WHERE $targetId = '${twinId}' OR $sourceId = '${twinId}'`
        : `SELECT * FROM RELATIONSHIPS WHERE $${relationshipDirection}Id = '${twinId}'`
    const queryResult = this._client?.queryTwins(query).byPage()
    if (!queryResult) return
    for await (const page of queryResult) {
      if (page.value) await callback(page.value as BasicRelationship[])
    }
  }

  public async queryRelationships(twinId: string, type = REL_TYPE_OUTGOING) {
    const list: BasicRelationship[] = []
    await this.queryRelationshipsPaged(twinId, (items) => items.forEach((x) => list.push(x)), type)

    return list
  }

  public async getRelationship<T extends BasicRelationship>(
    sourceTwinId: string,
    relationshipId: string
  ) {
    await this.initialize()
    const response = await this._client?.getRelationship(sourceTwinId, relationshipId)
    delete response?.etag
    return response as T
  }

  public async addRelationship<T extends BasicRelationship>({
    $sourceId,
    $targetId,
    $relationshipName,
    $relationshipId,
    ...properties
  }: BasicRelationship) {
    await this.initialize()

    const response = await this._client?.upsertRelationship($sourceId, $relationshipId, {
      $relationshipName,
      $targetId,
      ...properties
    })
    delete response?.etag
    return response as T
  }

  public async updateRelationship(twinId: string, relationshipId: string, patch: Operation[]) {
    await this.initialize()

    return this._client?.updateRelationship(
      twinId,
      relationshipId,
      patch as unknown as Array<Record<string, unknown>>
    )
  }

  public async getModels() {
    await this.initialize()

    const list: DigitalTwinsModelData[] = []
    const models = this._client?.listModels({
      dependenciesFor: [],
      includeModelDefinition: true
    })
    if (!models) return list
    for await (const model of models) {
      list.push(model as DigitalTwinsModelData)
    }

    return list
  }

  public async getModelById(modelId: string) {
    await this.initialize()

    return (await this._client?.getModel(modelId, {
      includeModelDefinition: true
    })) as DigitalTwinsModelData
  }

  public async addModels(models: DtdlInterface[]) {
    await this.initialize()

    try {
      return (await this._client?.createModels(models)) as DigitalTwinsModelData[]
    } catch (e) {
      // The Azure SDK's operation spec for POST /models only maps 201 to a success
      // response; every other status — including a perfectly valid 2xx — is routed to
      // the `default` (error) mapper and surfaces as a RestError whose message is the
      // raw response body. Konnektr Graph answers with 200, so a successful create was
      // reported to the user as an error showing the created models as JSON (AB#4313).
      const created = modelsFromSuccessfulRestError(e)
      if (!created) throw e
      return created
    }
  }

  public async deleteRelationship(twinId: string, relationshipId: string) {
    await this.initialize()

    return this._client?.deleteRelationship(twinId, relationshipId)
  }

  /** Delete a digital twin and its relationships */
  public async deleteTwin(twinId: string, skipRelationships = false) {
    // if not online, we should send this as a batch request to the batch endpoint (so that the SW can run this again)

    if (!skipRelationships) {
      await this.deleteTwinRelationships(twinId)
    }

    await this._client?.deleteDigitalTwin(twinId)
  }

  public async deleteTwinRelationships(twinId: string) {
    await this.initialize()

    const rels = await this.queryRelationships(twinId, REL_TYPE_ALL)
    for (const r of rels) {
      await this.deleteRelationship(r.$sourceId, r.$relationshipId)
    }
  }

  public async deleteModel(id: string) {
    await this.initialize()
    await this._client?.deleteModel(id)
  }
  public async decommissionModel(modelId: string) {
    await this.initialize()
    return await this._client?.decomissionModel(modelId)
  }

  private async initialize() {
    const { environmentId, environment } = useGlobalStore()
    if (!environmentId) throw new Error('Environment id not found')

    const adtHost = environment?.adtHost
    if (!adtHost) throw new Error('ADT host not defined')

    this._client = digitalTwinsClientFactory(environmentId, adtHost)
  }
}

export const twinsApiService = new TwinsApiService()
